/**
 * Cloudflare Turnstile invisible-widget wrapper.
 *
 * Lazy-loads the script on first need, renders an invisible widget,
 * and exposes execute() that resolves with a fresh token. Tokens are
 * good for 5 min so we call execute() right before each start-run.
 *
 * If Turnstile is disabled server-side (sitekey not configured),
 * execute() resolves to null — the run still starts, the server just
 * skips token verification.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, opts: TurnstileOpts) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    __turnstileReady?: () => void;
  }
}

interface TurnstileOpts {
  sitekey: string;
  size?: "normal" | "compact" | "invisible";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  "timeout-callback"?: () => void;
  appearance?: "always" | "execute" | "interaction-only";
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileReady";

export class TurnstileGuard {
  private sitekey: string;
  private widgetId: string | null = null;
  private container: HTMLElement;
  private scriptReady: Promise<void>;
  private pendingResolve: ((tok: string | null) => void) | null = null;

  constructor(sitekey: string) {
    this.sitekey = sitekey;

    this.container = document.createElement("div");
    this.container.style.cssText = "position:fixed;left:-9999px;top:0;width:0;height:0;";
    document.body.appendChild(this.container);

    this.scriptReady = new Promise<void>((resolve) => {
      window.__turnstileReady = () => resolve();
    });

    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  private async ensureWidget() {
    await this.scriptReady;
    if (this.widgetId || !window.turnstile) return;
    this.widgetId = window.turnstile.render(this.container, {
      sitekey: this.sitekey,
      size: "invisible",
      appearance: "execute",
      callback: (token: string) => {
        const r = this.pendingResolve;
        this.pendingResolve = null;
        r?.(token);
      },
      "error-callback": () => {
        const r = this.pendingResolve;
        this.pendingResolve = null;
        r?.(null);
      },
      "expired-callback": () => {
        if (this.widgetId) window.turnstile?.reset(this.widgetId);
      },
    });
  }

  /** Returns a fresh token, or null on error/timeout. iOS Safari sometimes
   *  stalls the widget — the shorter default keeps Begin Drift responsive
   *  by falling through to a token-less run-start (server rejects with 400,
   *  but only after we've started the game-loop visually). */
  async execute(timeoutMs?: number): Promise<string | null> {
    await this.ensureWidget();
    if (!this.widgetId || !window.turnstile) return null;

    // Shorter timeout on coarse-pointer (touch) devices — they're where
    // Turnstile stalls most often in our observed cases.
    const isCoarse = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;
    const t = timeoutMs ?? (isCoarse ? 3000 : 8000);

    return new Promise<string | null>((resolve) => {
      this.pendingResolve = resolve;
      window.turnstile!.execute(this.widgetId!);
      setTimeout(() => {
        if (this.pendingResolve === resolve) {
          this.pendingResolve = null;
          resolve(null);
        }
      }, t);
    });
  }
}
